import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxOption } from '@onyx/angular';
import {
  OnyxButtonComponent,
  OnyxLanguagePipe,
  OnyxSectionComponent,
  onyxDateValidator,
} from '@onyx/angular';
import { chain } from 'lodash';
import { map, shareReplay, startWith, switchMap } from 'rxjs';
import { CompanyDictionaryCode } from '../../../management-panel/dictionaries/common/enums/company-dictionary-code';
import type { CompanyDriverPermission } from '../../../management-panel/dictionaries/common/interfaces/company-driver-permission';
import { CompanyDictionariesService } from '../../../management-panel/dictionaries/common/services/company-dictionaries.service';
import type { DriverPermissionFormGroup } from './driver-permission-form/driver-permission-form.component';
import { DriverPermissionFormComponent } from './driver-permission-form/driver-permission-form.component';

type DriverPermissionsFormGroup = ReturnType<
  typeof DriverPermissionsFormComponent.buildForm
>;

@Component({
  selector: 'app-driver-permissions-form',
  imports: [
    OnyxSectionComponent,
    TranslatePipe,
    OnyxButtonComponent,
    AsyncPipe,
    DriverPermissionFormComponent,
  ],
  templateUrl: './driver-permissions-form.component.html',
  styleUrl: './driver-permissions-form.component.scss',
})
export class DriverPermissionsFormComponent {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly companyDictionariesService = inject(
    CompanyDictionariesService,
  );
  private readonly languagePipe = inject(OnyxLanguagePipe);
  private readonly destroyRef = inject(DestroyRef);

  public readonly form = input.required<DriverPermissionsFormGroup>();

  protected readonly I18N = 'drivers.driverForm.permissions';

  protected readonly permissions$ =
    this.companyDictionariesService.reload$.pipe(
      startWith(null),
      switchMap(() =>
        this.companyDictionariesService
          .listDictionary(CompanyDictionaryCode.DRIVER_PERMISSION)
          .pipe(
            map((permissions) =>
              chain(permissions)
                .map(
                  (
                    permission,
                  ): OnyxOption<string> & {
                    permission: CompanyDriverPermission;
                  } => ({
                    value: permission.uuid,
                    name: this.languagePipe.transform(permission.names),
                    leftIcon: {
                      name: permission.icon ?? 'requirements-other-filled',
                      size: 20,
                    },
                    permission,
                  }),
                )
                .groupBy(({ permission }) => permission.isDefault)
                .values()
                .map((options) => ({
                  id: options[0].permission.isDefault ? `0.default` : `1.other`,
                  options,
                }))
                .value(),
            ),
          ),
      ),
      shareReplay(1),
      takeUntilDestroyed(this.destroyRef),
    );

  protected addPermission(): void {
    this.form().push(DriverPermissionFormComponent.buildForm(this.fb));
  }

  protected removePermission(index: number): void {
    this.form().removeAt(index);
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.array<DriverPermissionFormGroup>([]);
  }

  public static buildPermissionForm(fb: NonNullableFormBuilder) {
    return fb.group({
      type: fb.control<string | null>(null, [Validators.required]),
      expirationDate: fb.control<string | null>(null, [onyxDateValidator]),
      scans: fb.control<string[]>([]),
    });
  }
}
